import { useEffect } from "react";
import { useTranslation } from "react-i18next";
import { Loader, Modal } from "ui";

import { usePreviewRecipients } from "../../apis";
import {
  CHANNEL_COLORS,
  CHANNEL_LABELS,
  SKIP_REASON_LABELS
} from "../../constants";
import { SegmentDefinition } from "../../marketing.types";
import { StatusBadge } from "../StatusBadge";

import styles from "./RecipientsPreviewModal.module.scss";

interface RecipientsPreviewModalProps {
  isOpen: boolean;
  definition: SegmentDefinition;
  onClose: () => void;
}

const WOULD_SEND_COLOR = "#16C350";
const SKIP_COLOR = "#EF4444";

const RecipientsPreviewModal = ({
  isOpen,
  definition,
  onClose
}: RecipientsPreviewModalProps) => {
  const { t } = useTranslation();

  const {
    mutate,
    data: recipients = [],
    isLoading,
    reset
  } = usePreviewRecipients();

  useEffect(() => {
    if (isOpen) {
      mutate(definition);
    } else {
      reset();
    }
  }, [isOpen]);

  const wouldSendCount = recipients.filter(
    (recipient) => recipient.wouldSend
  ).length;

  return (
    <Modal
      isOpen={isOpen}
      title={t("Preview recipients")}
      footer={null}
      onClose={onClose}
    >
      {isLoading ? (
        <Loader />
      ) : (
        <div className={styles.preview}>
          <span className={styles.preview__summary}>
            {wouldSendCount} / {recipients.length}{" "}
            {t("would receive a message")}
          </span>

          {recipients.length === 0 && (
            <span className={styles.preview__empty}>
              {t("No contacts match this audience")}
            </span>
          )}

          {recipients.map((recipient) => (
            <div key={recipient.contactId} className={styles.preview__row}>
              <span className={styles.preview__name}>
                {recipient.name ?? `+${recipient.phoneNumber}`}
              </span>

              {recipient.channel && (
                <StatusBadge
                  label={CHANNEL_LABELS[recipient.channel]}
                  color={CHANNEL_COLORS[recipient.channel]}
                />
              )}

              {recipient.wouldSend ? (
                <StatusBadge label="Would send" color={WOULD_SEND_COLOR} />
              ) : (
                <StatusBadge
                  label={
                    recipient.skippedReason
                      ? SKIP_REASON_LABELS[recipient.skippedReason]
                      : "Skipped"
                  }
                  color={SKIP_COLOR}
                />
              )}
            </div>
          ))}
        </div>
      )}
    </Modal>
  );
};

export default RecipientsPreviewModal;
